import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Mail } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * An email "link" that doesn't throw people into the Mail app (most Mac users don't use it). Clicking opens a
 * small box with the address, a Copy button and an "Open email app" link. Closes on Escape or a click elsewhere.
 * `className` styles the trigger exactly like the link it replaces.
 */
export function EmailLink({
  email,
  subject,
  className,
  wrapperClassName,
  align = "left",
  onOpenApp,
  children,
}: {
  email: string;
  subject?: string;
  className?: string;
  /** Layout for the outer wrapper, e.g. to let a full-width button stretch. */
  wrapperClassName?: string;
  /** Which edge of the trigger the box lines up with. */
  align?: "left" | "right" | "center";
  /** Called after "Open email app" (e.g. to close the mobile menu). */
  onOpenApp?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const boxId = useId();
  const mailto = `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onPointer = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
    } catch {
      // No clipboard (old browser or blocked): the address is on screen to select by hand.
    }
  }

  return (
    <span ref={wrap} className={cn("relative inline-block", wrapperClassName)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={boxId}
        onClick={() => setOpen((v) => !v)}
        className={className}
      >
        {children}
      </button>
      {open && (
        <span
          id={boxId}
          role="dialog"
          aria-label="Email us"
          className={cn(
            "absolute top-full z-50 mt-2 flex w-max flex-col gap-2 rounded-xl border border-char bg-paper-raised p-3 font-sans text-sm font-normal text-char shadow-offset-sm",
            align === "left" && "left-0",
            align === "right" && "right-0",
            align === "center" && "left-1/2 -translate-x-1/2",
          )}
        >
          <span className="select-all px-1 font-mono text-[13px]">{email}</span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-char px-3 py-1.5 text-[13px] font-semibold text-paper-white hover:bg-char/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
              {copied ? "Copied" : "Copy email"}
            </button>
            <a
              href={mailto}
              onClick={() => {
                setOpen(false);
                onOpenApp?.();
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-char/20 px-3 py-1.5 text-[13px] font-semibold text-char hover:bg-char/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              Open email app
            </a>
          </span>
        </span>
      )}
    </span>
  );
}
